import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, Search, Upload, Trash2, Video, Image, Bike, Wrench, X, Play } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function PhotographerPage() {
  const [tab, setTab] = useState("scooters");
  const [scooters, setScooters] = useState([]);
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [scRes, ptRes] = await Promise.all([
        axios.get(`${API}/photographer/stock`, { headers }),
        axios.get(`${API}/photographer/parts`, { headers }),
      ]);
      setScooters(scRes.data);
      setParts(ptRes.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Eroare la incarcarea datelor");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUploadPhoto = async (scooterId, files) => {
    setUploading(scooterId);
    const token = localStorage.getItem("token");
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("photo_type", "general");
        await axios.post(`${API}/scooters/${scooterId}/photos`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      } catch (err) {
        toast.error(`Eroare upload: ${file.name}`);
      }
    }
    toast.success("Fotografii adaugate!");
    setUploading(null);
    fetchData();
  };

  const handleUploadVideo = async (scooterId, file) => {
    setUploading(scooterId);
    const token = localStorage.getItem("token");
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/scooters/${scooterId}/videos`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Video adaugat!");
    } catch (err) {
      toast.error("Eroare upload video");
    }
    setUploading(null);
    fetchData();
  };

  const handleDeletePhoto = async (scooterId, photoUrl) => {
    if (!confirm("Stergeti aceasta fotografie?")) return;
    try {
      const token = localStorage.getItem("token");
      const filename = photoUrl.split("/").pop();
      await axios.delete(`${API}/scooters/${scooterId}/photos/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fotografie stearsa");
      fetchData();
    } catch (err) {
      toast.error("Eroare la stergere");
    }
  };

  const handleDeleteVideo = async (scooterId, videoUrl) => {
    if (!confirm("Stergeti acest video?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/scooters/${scooterId}/videos?url=${encodeURIComponent(videoUrl)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Video sters");
      fetchData();
    } catch (err) {
      toast.error("Eroare la stergere video");
    }
  };

  const handleUploadPartPhoto = async (partId, files) => {
    setUploading(partId);
    const token = localStorage.getItem("token");
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/parts/${partId}/photos`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      } catch (err) {
        toast.error(`Eroare upload: ${file.name}`);
      }
    }
    toast.success("Fotografii adaugate!");
    setUploading(null);
    fetchData();
  };

  const filteredScooters = scooters.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.brand?.toLowerCase().includes(q) || s.model?.toLowerCase().includes(q) || s.scooter_id?.toLowerCase().includes(q);
  });

  const filteredParts = parts.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.part_id?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
  });

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><div className="animate-pulse text-primary">Se incarca...</div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="photographer-page">
        <div>
          <h1 className="font-heading font-black text-3xl flex items-center gap-3">
            <Camera className="w-8 h-8 text-primary" />
            Fotografii & Video
          </h1>
          <p className="text-muted-foreground mt-1">Adaugati fotografii si video pentru produse</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button variant={tab === "scooters" ? "default" : "outline"} onClick={() => setTab("scooters")} className={tab === "scooters" ? "bg-primary text-black" : ""}>
            <Bike className="w-4 h-4 mr-2" /> Scutere ({scooters.length})
          </Button>
          <Button variant={tab === "parts" ? "default" : "outline"} onClick={() => setTab("parts")} className={tab === "parts" ? "bg-primary text-black" : ""}>
            <Wrench className="w-4 h-4 mr-2" /> Piese ({parts.length})
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cauta..." className="pl-10" />
        </div>

        {/* Scooters Tab */}
        {tab === "scooters" && (
          <div className="space-y-4">
            {filteredScooters.map(s => (
              <Card key={s.scooter_id} className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold">{s.brand} {s.model} ({s.year})</p>
                      <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                        <span className="font-mono">{s.scooter_id}</span>
                        <span>{s.mileage?.toLocaleString()} km</span>
                        <span>{s.engine_cc} cc</span>
                        <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                      </div>
                      <div className="flex gap-3 text-sm mt-1">
                        <span>Pret vanzare: <strong className="text-primary">{s.selling_price} EUR</strong></span>
                        {s.min_selling_price > 0 && (
                          <span>Pret minim: <strong className="text-orange-400">{s.min_selling_price} EUR</strong></span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" multiple accept="image/*" className="hidden" onChange={e => handleUploadPhoto(s.scooter_id, Array.from(e.target.files))} />
                        <Button size="sm" variant="outline" asChild disabled={uploading === s.scooter_id}>
                          <span><Image className="w-4 h-4 mr-1" /> {uploading === s.scooter_id ? "..." : "Foto"}</span>
                        </Button>
                      </label>
                      <label className="cursor-pointer">
                        <input type="file" accept="video/*" className="hidden" onChange={e => handleUploadVideo(s.scooter_id, e.target.files[0])} />
                        <Button size="sm" variant="outline" asChild disabled={uploading === s.scooter_id}>
                          <span><Video className="w-4 h-4 mr-1" /> Video</span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {/* Photos */}
                  {s.photos?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-muted-foreground mb-1"><Image className="w-3 h-3 inline mr-1" />{s.photos.length} fotografii</p>
                      <div className="flex gap-2 flex-wrap">
                        {s.photos.map((url, i) => (
                          <div key={`ph-${url.slice(-10)}`} className="relative group">
                            <img src={url} alt="" className="w-20 h-20 object-cover rounded border border-border" />
                            <button onClick={() => handleDeletePhoto(s.scooter_id, url)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {s.videos?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1"><Video className="w-3 h-3 inline mr-1" />{s.videos.length} video</p>
                      <div className="flex gap-2 flex-wrap">
                        {s.videos.map((url, i) => (
                          <div key={`vd-${url.slice(-10)}`} className="relative group">
                            <div className="w-20 h-20 bg-secondary rounded border border-border flex items-center justify-center">
                              <Play className="w-6 h-6 text-primary" />
                            </div>
                            <button onClick={() => handleDeleteVideo(s.scooter_id, url)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!s.photos || s.photos.length === 0) && (!s.videos || s.videos.length === 0) && (
                    <p className="text-xs text-red-400">Nicio fotografie sau video</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Parts Tab */}
        {tab === "parts" && (
          <div className="space-y-4">
            {filteredParts.map(p => (
              <Card key={p.part_id} className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold">{p.name}</p>
                      <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                        <span className="font-mono">{p.part_id}</span>
                        <span>{p.category}</span>
                        <span>{p.price} EUR</span>
                        <span>Stoc: {p.quantity}</span>
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <input type="file" multiple accept="image/*" className="hidden" onChange={e => handleUploadPartPhoto(p.part_id, Array.from(e.target.files))} />
                      <Button size="sm" variant="outline" asChild disabled={uploading === p.part_id}>
                        <span><Image className="w-4 h-4 mr-1" /> Foto</span>
                      </Button>
                    </label>
                  </div>
                  {p.photos?.length > 0 ? (
                    <div className="flex gap-2 flex-wrap">
                      {p.photos.map((url, i) => (
                        <img key={`pph-${url.slice(-10)}`} src={url} alt="" className="w-16 h-16 object-cover rounded border border-border" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-red-400">Nicio fotografie</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
